/**
 * "Which question do we never ask?", as a screen.
 *
 * The co-pilot has always known, during a call, whether the budget has come up.
 * It has never been able to say whether the budget comes up on a third of calls
 * or on all of them - and that second number is the one that changes how a sales
 * team works, because it is the only one that can be acted on before the call
 * rather than during it.
 *
 * A matrix rather than a list: the team's own share answers "do we ask", and
 * the columns beside it answer "or is it one person" - which is a different
 * conversation with a different fix, and reading only the first would send a
 * manager to talk to everybody about a habit two of them already have.
 *
 * Read-only, like the block on a call. Nothing on this screen edits anything.
 */
import { useCallback, useEffect, useState } from "react";
import { ListChecks, Loader2, TriangleAlert } from "lucide-react";
import { cn } from "@wa/ui";
import { useT } from "../../../../i18n/I18nProvider";
import { getCallCoverage, type CoverageReport } from "../../../../lib/callCoverage";
import { EmptyState, fmtNum } from "../../../ui";
import { RailTrigger } from "../../_shared/workMobile";
import {
  COVERAGE_DAYS,
  coverageMatrix,
  coveragePeriod,
  sharePercent,
  type CoverageCell,
  type CoverageDays,
} from "./matrix";

/**
 * How strongly a cell is drawn.
 *
 * Three bands rather than a gradient: the number is already on screen, and a
 * continuous colour ramp invites reading a four-point difference as meaningful
 * when the denominator is eleven calls. A cell with no calls behind it gets no
 * band at all - see `sharePercent`, which draws it as a dash.
 */
function band(cell: CoverageCell): string {
  if (cell.share === null) return "is-none";
  if (cell.share >= 0.66) return "is-high";
  if (cell.share >= 0.33) return "is-mid";
  return "is-low";
}

export function CoveragePanel({ onRail }: { onRail: () => void }) {
  const t = useT();
  const [days, setDays] = useState<CoverageDays>(30);
  const [report, setReport] = useState<CoverageReport | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setReport(null);
    try {
      // `new Date()` at fetch time rather than in state: a desk left open
      // overnight would otherwise keep asking about yesterday.
      const { from, to } = coveragePeriod(days, new Date());
      setReport(await getCallCoverage(from, to));
      setError(null);
    } catch {
      setError(t("calls.coverage.loadError"));
    }
  }, [days, t]);

  useEffect(() => {
    void load();
  }, [load]);

  const matrix = report ? coverageMatrix(report) : null;

  return (
    <div className="crm-card crm-work-col">
      <div className="crm-pane-head pt-3.5">
        <RailTrigger label={t("calls.pane.views")} onOpen={onRail} />
        <span className="crm-cov-head">
          <ListChecks className="size-4" />
          {t("calls.coverage.title")}
        </span>
        {report === null && !error ? (
          <Loader2 className="size-4 animate-spin text-muted-foreground" />
        ) : null}
        <div className="crm-seg crm-cov-seg ml-auto">
          {COVERAGE_DAYS.map((option) => (
            <button
              key={option}
              type="button"
              className={cn("crm-seg-b", days === option && "is-on")}
              onClick={() => setDays(option)}
              aria-pressed={days === option}
            >
              {t("calls.coverage.days", { n: option })}
            </button>
          ))}
        </div>
      </div>

      <div className="crm-cov-body">
        {error ? (
          <div className="mb-2 flex items-center gap-2 text-[12.5px] text-destructive">
            <TriangleAlert className="size-3.5 shrink-0" />
            {error}
          </div>
        ) : null}

        {report?.truncated ? (
          <div className="mb-2 flex items-center gap-2 text-[12.5px] text-destructive">
            <TriangleAlert className="size-3.5 shrink-0" />
            <span>{t("calls.coverage.truncated")}</span>
          </div>
        ) : null}

        {matrix && matrix.reads === 0 ? (
          <EmptyState
            icon={ListChecks}
            title={t("calls.coverage.emptyTitle")}
            body={t("calls.coverage.emptyNote")}
          />
        ) : null}

        {matrix && matrix.reads > 0 ? (
          <>
            <p className="crm-cov-lead">
              {t("calls.coverage.readCalls", { n: matrix.reads })}
            </p>
            {/* The table scrolls inside its own box. One column per operator on
                a narrow reading pane is exactly how a page ends up scrolling
                sideways as a whole, which moves the rail off screen too. */}
            <div className="crm-cov-scroll">
              <table className="crm-cov-tbl">
                <thead>
                  <tr>
                    <th scope="col">{t("calls.coverage.question")}</th>
                    <th scope="col" className="is-team">
                      {t("calls.coverage.team")}
                      <span>{fmtNum(matrix.reads)}</span>
                    </th>
                    {matrix.columns.map((column) => (
                      <th key={column.userId || "unclaimed"} scope="col">
                        {column.name ?? t("calls.coverage.unclaimed")}
                        <span>{fmtNum(column.reads)}</span>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {matrix.rows.map((row) => (
                    <tr key={row.id}>
                      <th scope="row">{row.question}</th>
                      <td className={cn("is-team", band(row.team))}>
                        {sharePercent(row.team.share)}
                      </td>
                      {row.cells.map((cell, ix) => (
                        <td
                          key={matrix.columns[ix]?.userId || `col-${ix}`}
                          className={band(cell)}
                        >
                          {sharePercent(cell.share)}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        ) : null}
      </div>
    </div>
  );
}
